import { DataPointArray, Mutable } from '@evorait/pace-core';
import { on } from '@ngrx/store';
import * as _ from 'lodash';
import { Answers } from '../../../types/answers';
import { initialEditableFormElementsState } from '../../../types/editable-form-elements';
import { FormParticipantCopies } from '../../../types/form-participant-copies';
import { FormsConstants } from '../../../types/forms-constants';
import { FUNCTIONAL_ELEMENT_TYPE } from '../../../types/functional-element';
import { FunctionalElements } from '../../../types/functional-elements';
import {
    initialParticipantCopyState,
    ParticipantCopy,
    ParticipantCopyId,
    ParticipantParticulars,
} from '../../../types/participant-copy';
import { RadioOptions } from '../../../types/radio-options';
import { Signature } from '../../../types/signature';
import { AnswerUtilities } from '../../../utilities/answer-utilities';
import { FormUtilities } from '../../../utilities/form-utilities';
import {
    addOrUpdateParticipantsfromConfirmation,
    addParticipantCopy,
    checkAllParticipants,
    checkParticipantInModal,
    generateChecklistCopies,
    removeParticipantForm,
    removeParticipantFromConfirmationSuccess,
    saveParticipantParticulars,
    selectNextParticipant,
    selectParticipant,
    selectPreviousParticipant,
    updateCurrentlyEditedParticipant,
} from '../../actions/form.actions';
import { FormState } from '../../state/form.state';

export function removeParticipantReducer(
    state: Readonly<FormState>,
    props: {
        participant: ParticipantCopyId;
    },
): Readonly<FormState> {
    // fail safe - should be prohibited by UI anyways!
    if (props.participant === FormsConstants.MASTER_PARTICIPANT_ID) {
        throw new Error(`FormReducer: removeParticipantReducer: Trying to delete master copy, which is forbidden!`);
    }

    const participantCopies: Mutable<FormParticipantCopies> = {
        ...state.participantCopies,
    };

    delete participantCopies[props.participant];

    return {
        ...state,
        selectedParticipantCopyId: FormsConstants.MASTER_PARTICIPANT_ID,
        participantCopies,
    };
}

export function removeParticipantFormReducer(
    state: Readonly<FormState>,
    props: {
        participant: ParticipantCopyId;
    },
): Readonly<FormState> {
    const participantCopy: ParticipantCopy = {
        ...state.participantCopies[props.participant],
        ...initialEditableFormElementsState,
        hasCopy: false,
    };

    return {
        ...state,
        selectedParticipantCopyId: FormsConstants.MASTER_PARTICIPANT_ID,
        participantCopies: {
            ...state.participantCopies,
            [props.participant]: { ...participantCopy },
        },
    };
}

export function addParticipantCopyReducer(
    state: Readonly<FormState>,
    props: {
        id: ParticipantCopyId;
    },
): Readonly<FormState> {
    const newParticipantCopy: ParticipantCopy = {
        ...initialParticipantCopyState,
        id: props.id,
        sortIndex: state.participantCopyModal.nextParticipantCopySortIndex,
        selectedInParticipantCopiesModal: true,
    };

    return {
        ...state,
        participantCopies: {
            ...state.participantCopies,
            [props.id]: { ...newParticipantCopy },
        },
        participantCopyModal: {
            currentlyEditedParticipantId: props.id,
            nextParticipantCopySortIndex: state.participantCopyModal.nextParticipantCopySortIndex + 1,
        },
    };
}

export function saveParticipantParticularsReducer(
    state: Readonly<FormState>,
    props: {
        participant: ParticipantParticulars;
    },
): Readonly<FormState> {
    const participantCopy = state.participantCopies[props.participant.id];
    let participantCopies: FormParticipantCopies;
    if (participantCopy.hasCopy) {
        const customerSignature = Object.values(participantCopy.functionalElements).find(
            (f) =>
                f.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER ||
                // cosima signature for identifying the customer signature
                f.type === FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE,
        ) as Signature;
        if (typeof customerSignature === 'undefined') {
            throw new Error(`FormReducer: saveParticipantParticularsReducer: Could not find customer signature`);
        }
        // update signature for this customer with given first and last name
        const newCustomerSignature: Signature = {
            ...customerSignature,
            firstName: props.participant.firstName,
            lastName: props.participant.lastName,
            email: props.participant.email,
            emailRequired: !props.participant.notReceivingCredit,
        };

        participantCopies = {
            ...state.participantCopies,
            [props.participant.id]: {
                ...participantCopy,
                ...props.participant,
                functionalElements: {
                    ...participantCopy.functionalElements,
                    [newCustomerSignature.id]: newCustomerSignature,
                },
            },
        };
    } else {
        // if no copy exists yet, we only update the particulars
        participantCopies = {
            ...state.participantCopies,
            [props.participant.id]: {
                ...participantCopy,
                ...props.participant,
            },
        };
    }

    return {
        ...state,
        participantCopies,
    };
}

export function checkParticipantInModalReducer(
    state: Readonly<FormState>,
    props: {
        participant: ParticipantCopyId;
    },
): Readonly<FormState> {
    const newParticipant: ParticipantCopy = {
        ...state.participantCopies[props.participant],
        selectedInParticipantCopiesModal: !state.participantCopies[props.participant].selectedInParticipantCopiesModal,
    };

    return {
        ...state,
        participantCopies: { ...state.participantCopies, [props.participant]: newParticipant },
    };
}

/**
 * If none of the participants are selected - then all of them will be selected
 * if all of the participants are selected - then all of them should be not selected
 * If only a couple of participants are selected - when the user will click,
 * on the selectAll Button in the participant-copies.component then all of them should be selected.
 */

export function checkAllParticipantsReducer(state: Readonly<FormState>): Readonly<FormState> {
    const selectedParticipants = Object.values(state.participantCopies).filter(
        (participantCopy) => participantCopy.selectedInParticipantCopiesModal,
    );

    // select all participants if not all are selected.
    // Ignore the master checklist and the participants which have copies.
    const selectAll =
        selectedParticipants.length !==
        Object.values(state.participantCopies).filter(
            (participantCopy) =>
                !participantCopy.hasCopy && participantCopy.id !== FormsConstants.MASTER_PARTICIPANT_ID,
        ).length;

    const participantCopies: Mutable<FormParticipantCopies> = {
        [FormsConstants.MASTER_PARTICIPANT_ID]: state.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID],
    };

    Object.values(state.participantCopies).forEach((participantCopy) => {
        if (participantCopy.id === FormsConstants.MASTER_PARTICIPANT_ID) {
            return;
        }

        // if the participant has a copy - then do not allow the selection in the participantCopiesModal
        participantCopies[participantCopy.id] = {
            ...participantCopy,
            selectedInParticipantCopiesModal: participantCopy.hasCopy ? false : selectAll,
        };
    });

    return {
        ...state,
        participantCopies,
    };
}

export function selectParticipantReducer(
    state: Readonly<FormState>,
    props: { id: ParticipantCopyId },
): Readonly<FormState> {
    if (!state.participantCopies[props.id].hasCopy) {
        throw new Error(
            `FormReducer: selectParticipantReducer: Trying to select participant '${props.id}' which has no copy yet!`,
        );
    }

    return {
        ...state,
        selectedParticipantCopyId: props.id,
    };
}

export function updateCurrentlyEditedParticipantReducer(
    state: Readonly<FormState>,
    props: { id: ParticipantCopyId },
): Readonly<FormState> {
    return {
        ...state,
        participantCopyModal: {
            ...state.participantCopyModal,
            currentlyEditedParticipantId: props.id,
        },
    };
}

export function selectPreviousParticipantReducer(state: Readonly<FormState>): Readonly<FormState> {
    const participantCopies = Object.values(state.participantCopies).filter((c) => c.hasCopy);
    const participantCopyIndex = participantCopies.findIndex((c) => c.id === state.selectedParticipantCopyId);
    if (participantCopyIndex < 0) {
        throw new Error(
            `FormReducer: selectPreviousParticipantReducer: Could not find participant copy for id ${state.selectedParticipantCopyId}`,
        );
    }

    const newParticipantCopyIndex =
        participantCopyIndex === 0 ? participantCopies.length - 1 : participantCopyIndex - 1;
    const newParticipant = participantCopies[newParticipantCopyIndex];
    return {
        ...state,
        selectedParticipantCopyId: newParticipant.id,
    };
}

export function selectNextParticipantReducer(state: Readonly<FormState>): Readonly<FormState> {
    const participantCopies = Object.values(state.participantCopies).filter((c) => c.hasCopy);
    const participantCopyIndex = participantCopies.findIndex((c) => c.id === state.selectedParticipantCopyId);
    if (participantCopyIndex < 0) {
        throw new Error(
            `FormReducer: selectNextParticipantReducer: Could not find participant copy for id ${state.selectedParticipantCopyId}`,
        );
    }

    const newParticipantCopyIndex =
        participantCopyIndex === participantCopies.length - 1 ? 0 : participantCopyIndex + 1;

    const newParticipant = participantCopies[newParticipantCopyIndex];
    return {
        ...state,
        selectedParticipantCopyId: newParticipant.id,
    };
}

export function addOrUpdateParticipantsFromConfirmationReducer(
    state: Readonly<FormState>,
    props: {
        participants: ParticipantParticulars[];
    },
): Readonly<FormState> {
    const newParticipants: Mutable<Omit<FormParticipantCopies, 'master'>> = {};
    let sortIndex = state.participantCopyModal.nextParticipantCopySortIndex;

    props.participants.forEach((participant) => {
        if (typeof state.participantCopies[participant.id] === 'undefined') {
            newParticipants[participant.id] = {
                ...initialParticipantCopyState,
                ...participant,
                sortIndex: sortIndex++,
            };
        } else {
            newParticipants[participant.id] = {
                ...state.participantCopies[participant.id],
                ...participant,
            };
        }
    });

    return {
        ...state,
        participantCopies: {
            [FormsConstants.MASTER_PARTICIPANT_ID]: state.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID],
            ...newParticipants,
        },
        participantCopyModal: {
            ...state.participantCopyModal,
            nextParticipantCopySortIndex: sortIndex,
        },
    };
}

export function generateChecklistCopiesReducer(
    state: Readonly<FormState>,
    props: {
        selectedParticipants: ParticipantCopyId[];
        employeeSignatureDataPoints: DataPointArray;
        employeeSignature: string;
        employeeFirstName: string;
        employeeLastName: string;
        employeeDate: string;
    },
): Readonly<FormState> {
    const participantCopies: Mutable<FormParticipantCopies> = {
        ...state.participantCopies,
    };

    const employeeSignatureFunctionalElement = FormUtilities.isCosimaForm(state.type)
        ? FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE
        : FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE;

    const customerSignatureFunctionalElement = FormUtilities.isCosimaForm(state.type)
        ? FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE
        : FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER;

    // Get participant copies that are selected in modal, copy editable elements from master into copy
    // and de-select participant in modal
    props.selectedParticipants.forEach((p) => {
        const participantCopy = state.participantCopies[p];

        // Save employee signature of copy and restore it after copying from master
        const employeeSignature = Object.values(
            state.participantCopies[state.selectedParticipantCopyId].functionalElements,
        ).find((f) => f.type === employeeSignatureFunctionalElement) as Signature;

        if (typeof employeeSignature === 'undefined') {
            throw new Error(
                `FormReducer: generateChecklistCopiesReducer: Could not find employee signature in participant copy!`,
            );
        }
        const newEmployeeSignature: Signature = {
            ...employeeSignature,
            signatureDataPoints: props.employeeSignatureDataPoints,
            signature: props.employeeSignature,
            firstName: props.employeeFirstName,
            lastName: props.employeeLastName,
            date: props.employeeDate,
        };

        // Save customer signature of copy and restore it after copying from master
        const customerSignature = Object.values(
            state.participantCopies[state.selectedParticipantCopyId].functionalElements,
        ).find((f) => f.type === customerSignatureFunctionalElement) as Signature;
        if (typeof customerSignature === 'undefined') {
            throw new Error(
                `FormReducer: generateChecklistCopiesReducer: Could not find customer signature in participant copy!`,
            );
        }
        const newCustomerSignature: Signature = {
            ...customerSignature,
            signature: '',
            signatureDataPoints: [],
            firstName: participantCopy.firstName,
            lastName: participantCopy.lastName,
            email: participantCopy.email,
            emailRequired: !participantCopy.notReceivingCredit,
        };

        // Update the answers state for each content block to reflect the updated answer state
        const newFunctionalElements: FunctionalElements = {
            ..._.cloneDeep(state.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID].functionalElements),
            [employeeSignature.id]: newEmployeeSignature,
            [customerSignature.id]: newCustomerSignature,
        };

        const newOptions: RadioOptions = _.cloneDeep(
            state.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID].options,
        );

        const newAnswers: Answers = AnswerUtilities.getCompleteAnswers(
            participantCopy.id,
            state.content,
            state.checkpoints,
            newOptions,
            newFunctionalElements,
            state.checkpointDependencies,
            state.typeFilters,
            state.formFilters,
            state.enableManualIndividualization,
            state.type,
        );

        participantCopies[p] = {
            ...participantCopy,
            answers: newAnswers,
            customerIdentification: _.cloneDeep(
                state.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID].customerIdentification,
            ),
            functionalElements: newFunctionalElements,
            options: newOptions,
            xml: state.participantCopies[FormsConstants.MASTER_PARTICIPANT_ID].xml,
            selectedInParticipantCopiesModal: false,
            hasCopy: true,
        };
    });

    return {
        ...state,
        participantCopies,
    };
}

export const participantCopiesReducer = [
    on<FormState, Array<typeof removeParticipantFromConfirmationSuccess>>(
        removeParticipantFromConfirmationSuccess,
        removeParticipantReducer,
    ),
    on<FormState, Array<typeof removeParticipantForm>>(removeParticipantForm, removeParticipantFormReducer),
    on<FormState, Array<typeof addParticipantCopy>>(addParticipantCopy, addParticipantCopyReducer),
    on<FormState, Array<typeof saveParticipantParticulars>>(
        saveParticipantParticulars,
        saveParticipantParticularsReducer,
    ),
    on<FormState, Array<typeof checkParticipantInModal>>(checkParticipantInModal, checkParticipantInModalReducer),
    on<FormState, Array<typeof checkAllParticipants>>(checkAllParticipants, checkAllParticipantsReducer),
    on<FormState, Array<typeof selectParticipant>>(selectParticipant, selectParticipantReducer),
    on<FormState, Array<typeof updateCurrentlyEditedParticipant>>(
        updateCurrentlyEditedParticipant,
        updateCurrentlyEditedParticipantReducer,
    ),
    on<FormState, Array<typeof selectPreviousParticipant>>(selectPreviousParticipant, selectPreviousParticipantReducer),
    on<FormState, Array<typeof selectNextParticipant>>(selectNextParticipant, selectNextParticipantReducer),
    on<FormState, Array<typeof generateChecklistCopies>>(generateChecklistCopies, generateChecklistCopiesReducer),
    on<FormState, Array<typeof addOrUpdateParticipantsfromConfirmation>>(
        addOrUpdateParticipantsfromConfirmation,
        addOrUpdateParticipantsFromConfirmationReducer,
    ),
];
